import type { ProfileChangeRow } from 'ui/ProfileChangeList';

import type { TeacherEditFormValues } from '../types';

const scalarKeys: (keyof TeacherEditFormValues)[] = [
  'name',
  'surname',
  'patronymic',
  'birthDate',
  'formatOnline',
  'formatOffline',
  'about',
  'trialLesson',
];

export const hasTeacherFormChanges = (
  values: TeacherEditFormValues,
  initial: TeacherEditFormValues,
): boolean => {
  for (const key of scalarKeys) {
    if (values[key] !== initial[key]) return true;
  }

  if (values.profilePhoto instanceof File) return true;

  if (values.educations.length !== initial.educations.length) return true;

  for (let index = 0; index < values.educations.length; index += 1) {
    const edu = values.educations[index];
    const initialEdu = initial.educations[index];
    if (!initialEdu) return true;

    if (
      edu.university !== initialEdu.university ||
      edu.faculty !== initialEdu.faculty ||
      edu.specialization !== initialEdu.specialization ||
      edu.startYear !== initialEdu.startYear ||
      edu.endYear !== initialEdu.endYear ||
      edu.testLesson !== initialEdu.testLesson ||
      edu.token !== initialEdu.token
    ) {
      return true;
    }

    if (edu.eduPhoto instanceof File) return true;
  }

  if (values.teacher_prices.length !== initial.teacher_prices.length) return true;

  for (let index = 0; index < values.teacher_prices.length; index += 1) {
    const price = values.teacher_prices[index];
    const initialPrice = initial.teacher_prices[index];
    if (!initialPrice) return true;
    if (
      price.subject !== initialPrice.subject ||
      price.service !== initialPrice.service ||
      String(price.price) !== String(initialPrice.price)
    ) {
      return true;
    }
  }

  return false;
};

function clip(value: string): string {
  const text = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return ' - ';
  return text.length > 180 ? `${text.slice(0, 177).trim()}...` : text;
}

function yn(value: boolean): string {
  return value ? 'да' : 'нет';
}

export function teacherFormChangeRows(
  values: TeacherEditFormValues,
  initial: TeacherEditFormValues,
  serviceTitle?: (slug: string) => string,
): ProfileChangeRow[] {
  const rows: ProfileChangeRow[] = [];
  const scalars: { field: keyof TeacherEditFormValues; label: string }[] = [
    { field: 'name', label: 'Имя' },
    { field: 'surname', label: 'Фамилия' },
    { field: 'patronymic', label: 'Отчество' },
    { field: 'birthDate', label: 'Дата рождения' },
    { field: 'about', label: 'О себе' },
  ];
  scalars.forEach(({ field, label }) => {
    const before = String(initial[field] || '');
    const after = String(values[field] || '');
    if (before === after) return;
    rows.push({
      field,
      label,
      change_type: 'modified',
      before: clip(before),
      after: clip(after),
    });
  });
  if (values.formatOnline !== initial.formatOnline) {
    rows.push({
      field: 'formatOnline',
      label: 'Формат: онлайн',
      before: yn(initial.formatOnline),
      after: yn(values.formatOnline),
    });
  }
  if (values.formatOffline !== initial.formatOffline) {
    rows.push({
      field: 'formatOffline',
      label: 'Формат: очно',
      before: yn(initial.formatOffline),
      after: yn(values.formatOffline),
    });
  }
  if (values.trialLesson !== initial.trialLesson) {
    rows.push({
      field: 'trialLesson',
      label: 'Пробное занятие',
      before: initial.trialLesson === 'agree' ? 'да' : 'нет',
      after: values.trialLesson === 'agree' ? 'да' : 'нет',
    });
  }
  if (values.profilePhoto instanceof File) {
    rows.push({
      field: 'profilePhoto',
      label: 'Фото профиля',
      before: initial.profilePhoto ? 'загружено' : 'не загружено',
      after: 'новое фото',
    });
  }
  const eduLine = (list: TeacherEditFormValues['educations']) =>
    list
      .map((item) => [item.university, item.faculty, item.specialization].filter(Boolean).join(', '))
      .filter(Boolean)
      .join('; ') || ' - ';
  const eduChanged =
    values.educations.length !== initial.educations.length ||
    values.educations.some((item, index) => {
      const prev = initial.educations[index];
      if (!prev) return true;
      return (
        item.university !== prev.university ||
        item.faculty !== prev.faculty ||
        item.specialization !== prev.specialization ||
        item.startYear !== prev.startYear ||
        item.endYear !== prev.endYear ||
        item.eduPhoto instanceof File
      );
    });
  if (eduChanged) {
    rows.push({
      field: 'educations',
      label: 'Образование',
      before: eduLine(initial.educations),
      after: eduLine(values.educations),
    });
  }
  const priceLine = (list: TeacherEditFormValues['teacher_prices']) =>
    list
      .map((item) => {
        const title = serviceTitle?.(item.service) || item.service || 'услуга';
        return item.price ? `${title}, ${item.price} ₽` : title;
      })
      .filter((line) => line && line !== 'услуга')
      .join('; ') || ' - ';
  const priceChanged =
    values.teacher_prices.length !== initial.teacher_prices.length ||
    values.teacher_prices.some((item, index) => {
      const prev = initial.teacher_prices[index];
      if (!prev) return true;
      return item.service !== prev.service || String(item.price) !== String(prev.price);
    });
  if (priceChanged) {
    rows.push({
      field: 'teacher_prices',
      label: 'Услуги и цены',
      before: priceLine(initial.teacher_prices),
      after: priceLine(values.teacher_prices),
    });
  }
  return rows;
}
